/**
 * Enterprise Permission Service
 * Provides policy-based authorization using the enterprise permissions system
 */

import { supabase } from '../utils/supabaseClient';

export interface AuthorizationRequest {
  profileId: string;
  tenantId: string;
  resourceName: string;
  actionName: string;
  context?: {
    supportSessionId?: string;
    partnerId?: string;
    requestMetadata?: Record<string, any>;
  };
}

export interface AuthorizationResponse {
  finalResult: 'ALLOW' | 'DENY' | 'NO_MATCH';
  isGranular: boolean;
  reason?: string;
  bypassReason?: string;
  rowCondition?: any;
  allowedColumns?: string[];
  columnMasks?: Array<{
    column: string;
    strategy: string;
    config: any;
  }>;
  tenantScope?: string[];
  enforcementFlags?: {
    abacRequired: boolean;
    rebacRequired: boolean;
    clsRequired: boolean;
    auditRequired: boolean;
  };
  processingTimeMs: number;
}

export interface Resource {
  resourceId: string;
  resourceName: string;
  resourceType: 'table' | 'api' | 'menu' | 'file' | 'report';
  resourceSubtype: string;
  tableName?: string;
  apiEndpoint?: string;
  isSensitive: boolean;
  resourceAttributes: Record<string, any>;
}

export interface Action {
  actionId: string;
  actionName: string;
  actionCode: string;
  description: string;
  isDestructive: boolean;
  actionAttributes: Record<string, any>;
}

export interface Policy {
  policyId: string;
  policyName: string;
  policyCode: string;
  description: string;
  policyType: 'system' | 'custom' | 'template';
  isActive: boolean;
  policyAttributes: Record<string, any>;
}

export interface PolicyStatement {
  statementId: string;
  resourceId: string;
  policyStatement: {
    actionId: string;
    effect: 'Allow' | 'Deny';
    rowCondition?: any;
    columnCondition?: {
      whitelist?: string[];
      blacklist?: string[];
    };
    timeCondition?: any;
    contextCondition?: any;
  };
  priority: number;
  isActive: boolean;
}

export class EnterprisePermissionService {
  /**
   * Main authorization function
   */
  async authorize(request: AuthorizationRequest): Promise<AuthorizationResponse> {
    try {
      const { data, error } = await supabase.rpc('base_authorize', {
        p_profile_id: request.profileId,
        p_tenant_id: request.tenantId,
        p_resource_name: request.resourceName,
        p_action_name: request.actionName,
        p_context: request.context || {}
      });

      if (error) {
        console.error('Authorization error:', error);
        throw new Error(`Authorization failed: ${error.message}`);
      }

      return data as AuthorizationResponse;
    } catch (error) {
      console.error('Enterprise permission service error:', error);
      throw error;
    }
  }

  /**
   * Batch authorization for multiple resource/action combinations
   */
  async authorizeBatch(
    profileId: string,
    tenantId: string,
    requests: Array<{ resourceName: string; actionName: string; context?: any }>
  ): Promise<Record<string, AuthorizationResponse>> {
    const results: Record<string, AuthorizationResponse> = {};
    
    // Execute all authorization requests in parallel
    const promises = requests.map(async (req) => {
      const key = `${req.resourceName}:${req.actionName}`;
      try {
        results[key] = await this.authorize({
          profileId,
          tenantId,
          resourceName: req.resourceName,
          actionName: req.actionName,
          context: req.context
        });
      } catch (error) {
        results[key] = {
          finalResult: 'DENY',
          isGranular: false,
          reason: 'authorization_error',
          processingTimeMs: 0
        };
      }
    });

    await Promise.all(promises);
    return results;
  }

  /**
   * Get all resources for a tenant
   */
  async getResources(tenantId: string): Promise<Resource[]> {
    const { data, error } = await supabase
      .from('base_resources')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('resource_name');

    if (error) throw error;
    return data || [];
  }

  /**
   * Get all actions for a tenant
   */
  async getActions(tenantId: string): Promise<Action[]> {
    const { data, error } = await supabase
      .from('base_actions')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('action_name');

    if (error) throw error;
    return data || [];
  }

  /**
   * Get all policies for a tenant
   */
  async getPolicies(tenantId: string): Promise<Policy[]> {
    const { data, error } = await supabase
      .from('base_policies')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('policy_name');

    if (error) throw error;
    return data || [];
  }

  /**
   * Get policy statements for a specific policy
   */
  async getPolicyStatements(tenantId: string, policyId: string): Promise<PolicyStatement[]> {
    const { data, error } = await supabase
      .from('base_policy_statements')
      .select(`
        statement_id,
        resource_id,
        policy_statement,
        priority,
        is_active,
        base_resources!inner(resource_name)
      `)
      .eq('tenant_id', tenantId)
      .eq('policy_id', policyId)
      .eq('is_active', true)
      .order('priority', { ascending: false });

    if (error) throw error;
    
    // Transform data to match PolicyStatement interface
    return (data || []).map((item: any) => ({
      statementId: item.statement_id,
      resourceId: item.resource_id,
      policyStatement: item.policy_statement || {},
      priority: item.priority,
      isActive: item.is_active
    }));
  }

  /**
   * Check if user has specific permission (convenience method)
   */
  async hasPermission(
    profileId: string,
    tenantId: string,
    resourceName: string,
    actionName: string,
    context?: any
  ): Promise<boolean> {
    try {
      const result = await this.authorize({
        profileId,
        tenantId,
        resourceName,
        actionName,
        context
      });
      
      return result.finalResult === 'ALLOW';
    } catch (error) {
      console.error('Permission check failed:', error);
      return false;
    }
  }

  /**
   * Get user's effective permissions for a resource
   */
  async getUserResourcePermissions(
    profileId: string,
    tenantId: string,
    resourceName: string
  ): Promise<Record<string, boolean>> {
    const actions = await this.getActions(tenantId);
    const permissions: Record<string, boolean> = {};

    const requests = actions.map(action => ({
      resourceName,
      actionName: action.actionName
    }));

    const results = await this.authorizeBatch(profileId, tenantId, requests);

    for (const [key, result] of Object.entries(results)) {
      const actionName = key.split(':')[1];
      permissions[actionName] = result.finalResult === 'ALLOW';
    }

    return permissions;
  }

  /**
   * Apply row conditions to a query (helper for data filtering)
   */
  applyRowConditions(baseQuery: any, rowCondition: any): any {
    if (!rowCondition) return baseQuery;

    // This would need to be implemented based on your specific query builder
    // For now, return the base query
    console.warn('Row condition application not yet implemented:', rowCondition);
    return baseQuery;
  }

  /**
   * Apply column masking to data (helper for data security)
   */
  applyColumnMasks(data: any[], columnMasks: AuthorizationResponse['columnMasks']): any[] {
    if (!columnMasks || columnMasks.length === 0) return data;

    return data.map(row => {
      const maskedRow = { ...row };
      
      columnMasks.forEach(mask => {
        if (maskedRow.hasOwnProperty(mask.column)) {
          switch (mask.strategy) {
            case 'null':
              maskedRow[mask.column] = null;
              break;
            case 'hash':
              maskedRow[mask.column] = '[HASHED]';
              break;
            case 'partial':
              const value = String(maskedRow[mask.column] || '');
              const keepChars = mask.config?.keep_chars || 2;
              maskedRow[mask.column] = value.substring(0, keepChars) + '*'.repeat(Math.max(0, value.length - keepChars));
              break;
            case 'encrypt':
              maskedRow[mask.column] = '[ENCRYPTED]';
              break;
            default:
              // Keep original value for unknown strategies
              break;
          }
        }
      });
      
      return maskedRow;
    });
  }
}

// Export singleton instance
export const enterprisePermissionService = new EnterprisePermissionService();
